if (!customElements.get('purchase-options')) {
	customElements.define('purchase-options', class PurchaseOptions extends HTMLElement {
		connectedCallback() {
			this.minicart = document.querySelector('mini-cart');
			this.addEventListener('click', this.onClick);

			if (typeof subscribe === 'function') {
				this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.productVariantChange, (data) => {
					if (data.sectionId === this.dataset.section) this.refresh(data.html);
				});
			}
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.onClick);
			if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
		}

		onClick = (event) => {
			const button = event.target.closest('.product-purchase-option-add');
			if (!button || !this.contains(button)) return;
			this.addOption(button.closest('.product-purchase-option'), button);
		};

		// Offers depend on the selected variant, so take the re-rendered markup
		refresh(html) {
			const source = html.getElementById(this.id);
			if (!source) return;
			this.innerHTML = source.innerHTML;
			this.hidden = source.hasAttribute('hidden');
		}

		// Uses the page quantity, clamped to the option's min/max
		getQuantity(option) {
			const input = document.getElementById(`quantity-input-${this.dataset.section}`);
			let quantity = Math.max(parseInt(input?.value, 10) || 1, 1);
			const min = parseInt(option.dataset.minQuantity, 10);
			const max = parseInt(option.dataset.maxQuantity, 10);
			if (min) quantity = Math.max(quantity, min);
			if (max) quantity = Math.min(quantity, max);
			return quantity;
		}

		// Adds the option's variant to the cart, then applies its discount code on
		// top of any codes the cart already carries
		async addOption(option, button) {
			if (button.classList.contains('loading')) return;

			const { variantId, discountCode, lineProperties } = option.dataset;
			const sections = this.minicart ? this.minicart.getSectionsToRender().map((section) => section.section) : [];
			const sectionParams = { sections, sections_url: window.location.pathname };

			this.setError(option);
			button.classList.add('loading');
			button.setAttribute('aria-disabled', true);

			try {
				const added = await this.postJSON(`${routes.cart_add_url}.js`, {
					items: [{
						id: Number(variantId),
						quantity: this.getQuantity(option),
						properties: lineProperties ? JSON.parse(lineProperties) : {},
					}],
					...(discountCode ? {} : sectionParams),
				});
				if (added.status) throw new Error(added.description || added.message);

				let state = added;
				if (discountCode) {
					const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
					// A cart-specific PICKUP code only covers the lines it was created for, and
					// Shopify keeps one product discount per line; drop it so the cart page
					// regenerates one that also folds in this upgrade
					const codes = (cart.discount_codes || [])
						.map((discount) => discount.code)
						.filter((code) => !/^PICKUP(-|$)/i.test(code));
					if (!this.hasCode(codes, discountCode)) codes.push(discountCode);

					state = await this.postJSON(`${routes.cart_update_url}.js`, { discount: codes.join(','), ...sectionParams });
					if (state.status) throw new Error(state.description || state.message);
				}

				if (this.minicart && state.sections) this.minicart.renderContents(state);

				if (discountCode) {
					const applied = (state.discount_codes || []).find((discount) => this.hasCode([discount.code], discountCode));
					if (!applied || !applied.applicable) {
						this.setError(option, this.dataset.discountError);
						return;
					}
				}

				if (window.themeSettings?.redirectToCart) {
					location.href = routes.cart_url;
				} else if (this.minicart) {
					this.minicart.open(button);
				}

				if (typeof publish === 'function') {
					publish(PUB_SUB_EVENTS.cartItemAdd, { source: `PurchaseOptions-${option.dataset.offerType}`, items: added.items });
				}
			} catch (error) {
				console.error(error);
				this.setError(option, error.message || window.cartStrings?.error);
				if (this.minicart) this.minicart.updateSectionContents();
			} finally {
				button.classList.remove('loading');
				button.removeAttribute('aria-disabled');
			}
		}

		hasCode(codes, code) {
			return codes.some((item) => item.toUpperCase() === code.toUpperCase());
		}

		postJSON(url, body) {
			const config = fetchConfig('javascript');
			config.headers['X-Requested-With'] = 'XMLHttpRequest';
			config.body = JSON.stringify(body);
			return fetch(url, config).then((response) => response.json());
		}

		setError(option, message = '') {
			const error = option.querySelector('.product-purchase-option-error');
			if (!error) return;
			error.textContent = message;
			error.hidden = !message;
		}
	});
}
